import { useState, useMemo, useCallback } from "react";
import { Brand } from "@/data/brands";
import { useBrands } from "@/hooks/useBrands";
import { useNavigate } from "react-router-dom";
import { useLocalePath } from "@/hooks/useLocalePath";
import { Loader2, Search, BarChart3 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { m, AnimatePresence } from "framer-motion";
import { ZONES, computeBeeswarm, selectFeaturedBrands, getZoneForScore } from "@/utils/horizonLayout";
import HorizonZoneBackground from "@/components/horizon/HorizonZoneBackground";
import BrandDot from "@/components/horizon/BrandDot";
import ShowAllButton from "@/components/horizon/ShowAllButton";
import HorizonTooltip from "@/components/horizon/HorizonTooltip";

const CONTAINER_HEIGHT = 640;

const BrandHorizon = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredBrand, setHoveredBrand] = useState<Brand | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { data: brands = [], isLoading } = useBrands();

  // Compute featured brands and zone counts
  const { allFeaturedIds, hiddenCountByZone } = useMemo(() => {
    const allIds = selectFeaturedBrands(brands);
    const byZone: Record<string, number> = {};
    const featuredByZone: Record<string, number> = {};
    ZONES.forEach(z => { byZone[z.key] = 0; featuredByZone[z.key] = 0; });
    brands.forEach(b => {
      const zone = getZoneForScore(b.overallScore);
      byZone[zone.key]++;
      if (allIds.has(b.id)) featuredByZone[zone.key]++;
    });
    const hidden: Record<string, number> = {};
    ZONES.forEach(z => {
      hidden[z.key] = Math.max(0, byZone[z.key] - featuredByZone[z.key]);
    });
    return { allFeaturedIds: allIds, hiddenCountByZone: hidden };
  }, [brands]);

  const positions = useMemo(
    () => computeBeeswarm(brands, CONTAINER_HEIGHT, allFeaturedIds),
    [brands, allFeaturedIds]
  );

  const highlightedIds = useMemo(() => {
    if (!searchQuery.trim()) return new Set<string>();
    const q = searchQuery.toLowerCase();
    return new Set(brands.filter(b => b.name.toLowerCase().includes(q)).map(b => b.id));
  }, [searchQuery, brands]);

  const isSearching = searchQuery.trim().length > 0;

  const { getPath } = useLocalePath();
  const handleBrandClick = useCallback(
    (brandId: string) => navigate(getPath(`/brand/${brandId}`)),
    [navigate, getPath]
  );

  const toggleZone = useCallback((zoneKey: string) => {
    setExpandedZones(prev => {
      const next = new Set(prev);
      if (next.has(zoneKey)) next.delete(zoneKey);
      else next.add(zoneKey);
      return next;
    });
  }, []);

  if (isLoading) {
    return (
      <section id="horizon" className="py-20 px-6 bg-gradient-subtle overflow-hidden">
        <div className="container mx-auto flex items-center justify-center min-h-[400px]">
            <m.div
            className="flex flex-col items-center gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground font-medium">
              {language === "fr" ? "Chargement des marques..." : "Loading brands..."}
            </p>
          </m.div>
        </div>
      </section>
    );
  }

  return (
    <section id="horizon" className="py-16 md:py-20 px-6 bg-gradient-subtle overflow-hidden">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <m.div
          className="max-w-3xl mx-auto text-center mb-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6 }}
        >
          <m.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <BarChart3 className="w-4 h-4" />
            <span>{brands.length} {language === "fr" ? "marques analysées" : "brands analyzed"}</span>
          </m.div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-4" style={{ textWrap: "pretty" }}>
            {language === "fr" ? "L'Horizon des Marques" : "The Brand Horizon"}
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
            {language === "fr"
              ? "Visualisez d'un coup d'œil où se positionnent les marques sur notre échelle d'impact."
              : "See at a glance where brands stand on our impact scale."}
          </p>
        </m.div>


        {/* Vertical Scale */}
        <m.div
          className="relative mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <div className="overflow-hidden pb-4" onClick={() => setSelectedBrand(null)}>
            <div className="relative w-full" style={{ height: `${CONTAINER_HEIGHT}px` }}>
              <HorizonZoneBackground />

              {/* "Show all" buttons */}
              {ZONES.map(zone => (
                <ShowAllButton
                  key={zone.key}
                  zone={zone}
                  hiddenCount={hiddenCountByZone[zone.key]}
                  isExpanded={expandedZones.has(zone.key)}
                  onToggle={() => toggleZone(zone.key)}
                />
              ))}

              {/* Brand dots & logos */}
              <AnimatePresence>
                {brands.map((brand, index) => {
                  const pos = positions.get(brand.id);
                  if (!pos) return null;

                  const zone = getZoneForScore(brand.overallScore);
                  const isFeatured = allFeaturedIds.has(brand.id);
                  const isZoneExpanded = expandedZones.has(zone.key);
                  const showLogo = isFeatured || isZoneExpanded || (isSearching && highlightedIds.has(brand.id));
                  const isHighlighted = !isSearching || highlightedIds.has(brand.id);

                  return (
                    <BrandDot
                      key={brand.id}
                      brand={brand}
                      x={pos.x}
                      visualY={pos.visualY}
                      showLogo={showLogo}
                      isHighlighted={isHighlighted}
                      isSearching={isSearching}
                      isSelected={selectedBrand?.id === brand.id}
                      index={index}
                      onHover={setHoveredBrand}
                      onClick={handleBrandClick}
                      onSelect={(b) => { setSelectedBrand(b); setHoveredBrand(b); }}
                    />
                  );
                })}
              </AnimatePresence>

              {/* Hover / selected tooltip */}
              <AnimatePresence>
                {(hoveredBrand || selectedBrand) && (
                  <HorizonTooltip
                    brand={(selectedBrand || hoveredBrand)!}
                    position={positions.get((selectedBrand || hoveredBrand)!.id)}
                    onClick={selectedBrand ? handleBrandClick : undefined}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
        </m.div>
      </div>
    </section>
  );
};

export default BrandHorizon;
